import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatBadgeModule } from '@angular/material/badge';

import { MatTooltipModule } from '@angular/material/tooltip';

import { TranslatePipe } from 'app/core/i18n/translate.pipe';

import { SuggestedOrder, SuggestedOrderProduct } from '../../interfaces/suggested-order.interface';
import { DgNumberPipe } from '../../dg-number.pipe';
import { altUnitsOf } from '../../alt-units.util';
import { LineAltUnits } from '../../interfaces/dialog-data.interface';
import { DocumentGenerateConfigService } from '../../document-generate-config.service';
import { BomCellComponent } from '../../components/bom-cell/bom-cell.component';
import { ItemDetailModalComponent } from '../item-detail-modal/item-detail-modal.component';

@Component({
  selector: 'app-order-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatBadgeModule,
    DgNumberPipe,
    BomCellComponent,
    TranslatePipe,
  ],
  templateUrl: './order-detail-modal.component.html',
})
export class OrderDetailModalComponent implements OnInit, OnDestroy {
  selected = new Set<number>();
  order: SuggestedOrder;
  title: string;
  readonly: boolean;
  salesOfficeId?: string;
  showStock = false;
  umpActive = true;

  // en pantallas de poca altura (móvil horizontal) la información general no deja ver los productos
  shortScreen = false;
  private shortScreenSub?: Subscription;

  constructor(
    private dialogRef: MatDialogRef<OrderDetailModalComponent>,
    private dialog: MatDialog,
    public dgConfig: DocumentGenerateConfigService,
    private breakpointObserver: BreakpointObserver,
    @Inject(MAT_DIALOG_DATA)
    data:
      | SuggestedOrder
      | {
          order: SuggestedOrder;
          title?: string;
          readonly?: boolean;
          salesOfficeId?: string;
          showStock?: boolean;
          umpActive?: boolean;
        }
  ) {
    const wrapped = data && (data as any).order ? (data as any) : { order: data };
    this.order = wrapped.order;
    this.title = wrapped.title ?? 'Detalle de Documento Sugerido';
    this.readonly = wrapped.readonly ?? false;
    this.salesOfficeId = wrapped.salesOfficeId;
    this.showStock = wrapped.showStock ?? false;
    this.umpActive = wrapped.umpActive ?? true;
  }

  ngOnInit(): void {
    this.shortScreenSub = this.breakpointObserver
      .observe('(max-height: 560px)')
      .subscribe((state) => (this.shortScreen = state.matches));
  }

  ngOnDestroy(): void {
    this.shortScreenSub?.unsubscribe();
  }

  // contador de notas + adjuntos por línea
  detailCount(p: SuggestedOrderProduct): number {
    return (p.note ? 1 : 0) + (p.evidences?.length ?? 0);
  }

  // el almacén con el que se generó la línea
  warehouseLabel(p: SuggestedOrderProduct): string {
    const code = p.warehouseCode ?? '';
    const name = p.warehouseName ?? '';
    return code && name ? `${code} - ${name}` : code || name;
  }

  // abre el detalle de artículo en solo lectura (notas y adjuntos del documento)
  openDetail(p: SuggestedOrderProduct): void {
    this.dialog.open(ItemDetailModalComponent, {
      data: {
        item: {
          code: p.code,
          name: p.name,
          note: p.note,
          evidences: p.evidences ?? [],
          material: p.material,
          unitLabel: p.unitLabel,
          unitMeasureId: p.unitMeasureId,
        },
        readonly: true,
        showStock: false,
      },
      ...this.dgConfig.dialogSize('md'),
    });
  }

  printDocument(): void {
    this.dialogRef.close('print');
  }

  get attachments(): { name?: string; link?: string }[] {
    return (this.order.attachedFilePurchaseOrder ?? []).filter((f) => !!f?.link);
  }

  openAttachment(file: { link?: string }): void {
    if (file?.link) window.open(file.link, '_blank', 'noopener');
  }

  // la línea de regalo se muestra pero no se copia: la regenera el reprecio
  isBonusLine(p: SuggestedOrderProduct): boolean {
    return p.bonustParentPosition != null;
  }

  // unidades regaladas dentro de la misma línea comprada (INCLUDED_AS_DISCOUNT)
  includedBonusQty(p: SuggestedOrderProduct): number {
    return Number(p.bonusSoldQuantity) || 0;
  }

  // la línea comprada originó líneas de regalo aparte (SEPARATE_FREE_LINE)
  bonusChildPositions(p: SuggestedOrderProduct): number[] {
    return p.bonusChildPositions ?? [];
  }

  // la línea de regalo comparte código con su línea de origen, así que la clave es la posición
  private keyOf(p: SuggestedOrderProduct): number {
    return p.position ?? this.order.products.indexOf(p) + 1;
  }

  get selectableProducts(): SuggestedOrderProduct[] {
    return this.order.products.filter((p) => !this.isBonusLine(p));
  }

  // selección de productos a copiar
  isSelected(p: SuggestedOrderProduct): boolean {
    return this.selected.has(this.keyOf(p));
  }

  toggle(p: SuggestedOrderProduct): void {
    if (this.isBonusLine(p)) return;
    const key = this.keyOf(p);
    this.selected.has(key) ? this.selected.delete(key) : this.selected.add(key);
  }

  allSelected(): boolean {
    const selectable = this.selectableProducts;
    return selectable.length > 0 && selectable.every((p) => this.selected.has(this.keyOf(p)));
  }

  someSelected(): boolean {
    return this.selected.size > 0 && !this.allSelected();
  }

  toggleAll(checked: boolean): void {
    if (checked) {
      this.selectableProducts.forEach((p) => this.selected.add(this.keyOf(p)));
    } else {
      this.selected.clear();
    }
  }

  useAsBase(): void {
    const products = this.selectableProducts.filter((p) => this.selected.has(this.keyOf(p)));
    this.dialogRef.close(products);
  }

  taxLabel(p: SuggestedOrderProduct): string {
    return p.taxName || (p.taxPct ? p.taxPct + '%' : '-');
  }

  // conversión de la cantidad vendida a unidad secundaria y paralela; el material
  // del documento llega con sus unidades alternas, así que se calcula igual que en el formulario
  altUnits(p: SuggestedOrderProduct): LineAltUnits {
    return altUnitsOf(p.material, p.unitMeasureId, p.quantity, this.umpActive);
  }

  unitDisplay(p: SuggestedOrderProduct): string {
    return p.unitCode ? `${p.unitCode} - ${p.unitLabel}` : p.unitLabel;
  }

  rowSubtotal(p: SuggestedOrderProduct): number {
    return p.subtotal ?? p.quantity * p.price;
  }

  rowDiscount(p: SuggestedOrderProduct): number {
    return p.discount ?? 0;
  }

  rowIsv(p: SuggestedOrderProduct): number {
    return p.isv ?? 0;
  }

  rowTotal(p: SuggestedOrderProduct): number {
    return p.total ?? this.rowSubtotal(p) - this.rowDiscount(p) + this.rowIsv(p);
  }

  get totalQuantity(): number {
    return this.order.products.reduce((s, p) => s + p.quantity, 0);
  }

  get totalSubtotal(): number {
    return this.order.products.reduce((s, p) => s + this.rowSubtotal(p), 0);
  }

  get totalDiscount(): number {
    return this.order.products.reduce((s, p) => s + this.rowDiscount(p), 0);
  }

  get totalIsv(): number {
    return this.order.products.reduce((s, p) => s + this.rowIsv(p), 0);
  }

  get montoTotal(): number {
    return this.order.products.reduce((s, p) => s + this.rowTotal(p), 0);
  }

  close(): void {
    this.dialogRef.close();
  }
}
